import { endOf, now, startOf, today } from '@/utils/date';

/**
 * Noon is used for backdated operations — see `toOperationTimestamp`.
 */
const NOON_HOUR = 12;

/**
 * How far past `notBefore` we nudge when the default time would land on or
 * before it. One second is comfortably above the DB's timestamp resolution and
 * still reads as "the same moment" to a human.
 */
const NUDGE_MS = 1000;

export interface OperationTimestampOptions {
    /** Injectable "current instant", for tests. */
    reference?: Date;
    /**
     * The instant the result must land strictly after — the active location's
     * `fromDate` for the fish group(s) involved. Pass it wherever the endpoint
     * enforces `INVALID_OPERATION_DATE` (operate / relocate / close); omit it
     * for create, which opens the first location and has nothing to be after.
     */
    notBefore?: Date | null;
}

/**
 * Default for every fish-group action date picker: today, at local midnight
 * (the granularity `StyledSingleDatePicker` works in).
 *
 * Call it inside the `useForm` initialiser / `reset(...)` rather than storing
 * the result in a module constant — a constant would freeze "today" for the
 * lifetime of the tab.
 */
export function defaultOperationDate(): Date {
    return today();
}

/**
 * Convert a calendar date picked in `StyledSingleDatePicker` into an ISO
 * timestamp the fish-group endpoints will accept.
 *
 * The pickers hand back **local midnight**, which is wrong on two counts:
 *
 *  1. The backend rejects any date that is not *strictly after* the fish
 *     group's active location `fromDate` (`INVALID_OPERATION_DATE`). A group
 *     whose location opened earlier *today* would therefore 400 on the most
 *     common pick there is — today.
 *  2. Local midnight east of UTC serialises to the previous calendar day
 *     (Oslo 00:00 -> 22:00Z the day before), so anything reading the day back
 *     out of the ISO string sees the wrong date.
 *
 * So: picking today sends the current instant (always after anything written
 * earlier today), and backdating sends 12:00 local — no jurisdiction skips
 * noon, so it is immune to DST, and the calendar day survives the round-trip
 * through UTC.
 *
 * Those two defaults are not always after `notBefore`, though. The picker's
 * `minDate` is **day**-granular, so the location's own opening day is always
 * selectable — and a location that opened at or after noon rejects the noon we
 * would otherwise send for that day. Hence the nudge below.
 */
export function toOperationTimestamp(
    picked: Date,
    options: OperationTimestampOptions = {}
): string {
    const { reference = now(), notBefore = null } = options;

    const pickedDay = startOf(picked, 'day') ?? picked;
    const referenceDay = startOf(reference, 'day');
    const isToday = referenceDay !== null && pickedDay.getTime() === referenceDay.getTime();

    const candidate = isToday ? new Date(reference.getTime()) : atNoon(pickedDay);

    if (!notBefore || candidate.getTime() > notBefore.getTime()) {
        return candidate.toISOString();
    }

    // The picker only ever captured a calendar *day*, and we already synthesise
    // a time within it (noon / the current instant), so moving to another time
    // on that same day misrepresents nothing — it is the operator's day, at the
    // earliest instant the backend will accept. Clamping to a different *day*
    // would falsify the record; this does not.
    const nudged = new Date(notBefore.getTime() + NUDGE_MS);
    const endOfPickedDay = endOf(pickedDay, 'day');
    if (endOfPickedDay && nudged.getTime() > endOfPickedDay.getTime()) {
        // `notBefore` sits in the final second of the picked day, so NO instant
        // on this date satisfies the guard — the operation is genuinely
        // impossible on the date the operator chose.
        //
        // We knowingly return a value the backend will reject, and that is the
        // intended outcome, not an oversight: the only way to produce an
        // acceptable timestamp here is to cross midnight, which would record a
        // different calendar day than the one that was picked. A clean
        // INVALID_OPERATION_DATE beats silently filing the operation under the
        // wrong day.
        return candidate.toISOString();
    }
    return nudged.toISOString();
}

function atNoon(day: Date): Date {
    const noon = new Date(day.getTime());
    noon.setHours(NOON_HOUR, 0, 0, 0);
    return noon;
}
